import { CalendarClock, History, Plus, UserLock } from "lucide-react";
import { useEffect, useState } from "react";
import { InternalRateDialog } from "@/components/admin/InternalRateDialog";
import { InternalRateHistoryDialog } from "@/components/admin/InternalRateHistoryDialog";
import { describeSuggestion, formatRateMonth } from "@/components/admin/internal-rate-format";
import { DisciplineChip } from "@/components/shared/discipline-chip";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { SearchInput } from "@/components/shared/search-input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useCan } from "@/hooks/use-can";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { StepUpRequiredError, useResourceInternalRates } from "@/hooks/use-resource-internal-rates";
import { formatPlanMoney } from "@/lib/plan-format";
import type { ResourceInternalRateRowView } from "@/schemas/resource-internal-rates";

const PAGE_SIZE = 50;

/**
 * Configuration → Internal Rates (2026-10-14): each active Resource's own internal cost per
 * hour, effective by month, beside a suggestion from their strongest RM-confirmed Discipline.
 * Rendered only inside StepUpGate; every request carries its proof.
 */
export function InternalRatesManager({
  stepUpId,
  onExpired,
}: {
  stepUpId: string;
  onExpired: () => void;
}) {
  const canManage = useCan("manageResourceInternalRates");
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search, 300);
  const [withoutRate, setWithoutRate] = useState(false);
  // A stack of cursors: the top is the page on screen; Previous pops it.
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const cursor = cursors[cursors.length - 1] ?? null;
  const [adding, setAdding] = useState<ResourceInternalRateRowView | null>(null);
  const [viewing, setViewing] = useState<ResourceInternalRateRowView | null>(null);

  // A new filter starts from the first page (state adjusted during render, not in an effect).
  const filterKey = `${debounced}|${withoutRate}`;
  const [lastFilterKey, setLastFilterKey] = useState(filterKey);
  if (filterKey !== lastFilterKey) {
    setLastFilterKey(filterKey);
    setCursors([null]);
  }

  const query = useResourceInternalRates(stepUpId, { search: debounced, withoutRate, cursor });

  // The proof lapsed mid-visit — hand back to the gate to ask again.
  const expired = query.error instanceof StepUpRequiredError;
  useEffect(() => {
    if (expired) onExpired();
  }, [expired, onExpired]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold">Internal Rates</h2>
        <p className="text-sm text-muted-foreground">
          Each Resource's own cost per hour, from the month it starts until the next one. On a plan
          line that names the Resource it replaces the Labor Category cost in margin. Only Admin and
          Finance can see these figures.
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SearchInput
          value={search}
          onValueChange={setSearch}
          placeholder="Search Resources by name or email"
          className="sm:max-w-sm"
        />
        <div className="flex items-center gap-2">
          <Checkbox
            id="internal-rates-without-rate"
            checked={withoutRate}
            onCheckedChange={(v) => setWithoutRate(v === true)}
          />
          <Label htmlFor="internal-rates-without-rate" className="text-sm font-normal">
            Only Resources without a rate
          </Label>
        </div>
      </div>

      {query.isLoading ? (
        <div className="flex flex-col gap-2">
          {["a", "b", "c", "d", "e"].map((k) => (
            <Skeleton key={k} className="h-12 w-full" />
          ))}
        </div>
      ) : query.isError && !expired ? (
        <ErrorState
          message={query.error.message}
          onRetry={() => {
            query.refetch();
          }}
        />
      ) : query.data === undefined || query.data.data.length === 0 ? (
        <EmptyState
          icon={UserLock}
          title={withoutRate ? "Every Resource has a rate" : "No Resources match"}
          description={
            withoutRate
              ? "Clear the filter to see every Resource's current rate."
              : "Try a different name or email."
          }
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border border-border bg-card">
            <table className="w-full min-w-[960px] text-sm">
              <thead className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Resource</th>
                  <th className="px-3 py-2 font-medium">Disciplines</th>
                  <th className="px-3 py-2 text-right font-medium">Suggested</th>
                  <th className="px-3 py-2 text-right font-medium">Current rate</th>
                  <th className="px-3 py-2 font-medium">Scheduled</th>
                  <th className="px-3 py-2 text-right font-medium">Live plans</th>
                  <th className="px-3 py-2">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {query.data.data.map((row) => (
                  <tr key={row.resourceId} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2">
                      <div className="font-medium">{row.resourceName}</div>
                      <div className="text-xs text-muted-foreground">{row.email}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {row.disciplines.length === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          row.disciplines.map((d) => (
                            <DisciplineChip
                              key={d.code}
                              code={d.code}
                              title={
                                d.rating === null
                                  ? `${d.name}: not rated`
                                  : `${d.name}: rated ${d.rating}${d.confirmed ? ", confirmed" : ", not confirmed"}`
                              }
                            />
                          ))
                        )}
                      </div>
                    </td>
                    <td
                      className="px-3 py-2 text-right tabular-nums"
                      title={describeSuggestion(row.suggestion)}
                    >
                      {row.suggestion.kind === "suggested" ? (
                        <>
                          <div>{formatPlanMoney(row.suggestion.internalCost)}</div>
                          <div className="text-xs text-muted-foreground">
                            {row.suggestion.disciplineCode} · rated {row.suggestion.rating}
                          </div>
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {row.currentRate === null ? (
                        <span className="text-muted-foreground">Labor Category cost</span>
                      ) : (
                        <>
                          <div className="font-medium">
                            {formatPlanMoney(row.currentRate.internalCost)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            since {formatRateMonth(row.currentRate.effectiveMonth)}
                          </div>
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {row.nextRate === null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 tabular-nums">
                          <CalendarClock className="size-3.5 text-muted-foreground" aria-hidden />
                          {formatPlanMoney(row.nextRate.internalCost)} from{" "}
                          {formatRateMonth(row.nextRate.effectiveMonth)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.livePlanCount}</td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        {row.rateCount > 0 && (
                          <Button variant="ghost" size="sm" onClick={() => setViewing(row)}>
                            <History className="size-4" aria-hidden />
                            History
                          </Button>
                        )}
                        {canManage && (
                          <Button variant="outline" size="sm" onClick={() => setAdding(row)}>
                            <Plus className="size-4" aria-hidden />
                            Set rate
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <CursorPagination
            total={query.data.total}
            page={cursors.length}
            limit={PAGE_SIZE}
            hasNext={query.data.hasNext}
            hasPrev={cursors.length > 1}
            loading={query.isFetching}
            itemLabel="Resources"
            onNext={() => {
              const next = query.data?.nextCursor;
              if (next) setCursors((c) => [...c, next]);
            }}
            onPrev={() => setCursors((c) => (c.length > 1 ? c.slice(0, -1) : c))}
          />
        </>
      )}

      {adding !== null && (
        <InternalRateDialog
          open
          onOpenChange={(open) => {
            if (!open) setAdding(null);
          }}
          row={adding}
          stepUpId={stepUpId}
        />
      )}
      {viewing !== null && (
        <InternalRateHistoryDialog
          open
          onOpenChange={(open) => {
            if (!open) setViewing(null);
          }}
          row={viewing}
          stepUpId={stepUpId}
        />
      )}
    </div>
  );
}
